import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { DownloadSimple, FileXls, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Reports() {
  const { t } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/monthly", { params: { year, month } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/reports/export", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet_${year}_${String(month).padStart(2,'0')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPayroll = async () => {
    try {
      const res = await api.get("/payroll/export", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Prenos_eTM_${year}_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll XLSX generated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emailPayroll = async () => {
    const to = window.prompt(
      "Email payroll XLSX + PDF to (comma-separated):",
      "rok.kobal@europacific.com"
    );
    if (!to) return;
    try {
      const { data } = await api.post("/payroll/email", {
        year, month,
        to: to.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(`Sent to ${data.to.join(", ")} · ${data.rows} rows · ${data.attachments.join("+")}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadMonthlyCard = async () => {
    try {
      const res = await api.get("/payroll/monthly-card.pdf", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Mesecni_karton_${year}_${String(month).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Mesečni karton PDF generated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [payrollPreview, setPayrollPreview] = useState(null);
  const loadPayrollPreview = async () => {
    try {
      const { data } = await api.get("/payroll/preview", { params: { year, month } });
      setPayrollPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const months = [...Array(12)].map((_,i) => i+1);
  const years = [year-1, year, year+1];

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("reports")}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none">Monthly timesheet</h1>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("month")}</label>
            <select data-testid="report-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="brut-border p-2 bg-white">
              {months.map(m => <option key={m} value={m}>{new Date(2000, m-1, 1).toLocaleString(undefined, { month: "long" })}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("year")}</label>
            <select data-testid="report-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="brut-border p-2 bg-white">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button
            data-testid="export-btn"
            onClick={downloadCsv}
            className="bg-[#0047FF] text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
          >
            <DownloadSimple size={16} weight="bold" /> {t("export_csv")}
          </button>
          <button
            data-testid="payroll-btn"
            onClick={downloadPayroll}
            className="bg-[#00CC33] text-black brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
            title="Prenos eTM — payroll XLSX"
          >
            <FileXls size={16} weight="bold" /> Payroll XLSX
          </button>
          <button
            data-testid="monthly-card-btn"
            onClick={downloadMonthlyCard}
            className="bg-[#FF8000] text-black brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
            title="Mesečni karton — monthly card PDF"
          >
            <FilePdf size={16} weight="bold" /> Mesečni karton PDF
          </button>
          <button
            data-testid="payroll-email-btn"
            onClick={emailPayroll}
            className="bg-[#FF66B2] text-black brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
            title="Email the XLSX + PDF to payroll"
          >
            <PaperPlaneTilt size={16} weight="bold" /> Email payroll
          </button>
          <button
            data-testid="payroll-preview-btn"
            onClick={loadPayrollPreview}
            className="brut-border bg-white brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs"
          >
            Preview
          </button>
        </div>
      </div>

      <div className="brut-border bg-white brut-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("name")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("team")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("work_hours")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("break_hours")}</th>
                <th className="text-left p-3 border-b-2 border-black">WFH h</th>
                <th className="text-left p-3 border-b-2 border-black">{t("events")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("annual")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("sick")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("overtime")}</th>
              </tr>
            </thead>
            <tbody data-testid="reports-table">
              {(data?.rows || []).map((r) => (
                <tr key={r.user.id} className="border-b border-black/10">
                  <td className="p-3">
                    <div className="font-bold">{r.user.name}</div>
                    <div className="text-xs font-mono text-black/60">{r.user.email}</div>
                  </td>
                  <td className="p-3">{r.user.team || "—"}</td>
                  <td className="p-3 font-bold">{r.work_hours.toFixed(1)}</td>
                  <td className="p-3">{r.break_hours.toFixed(1)}</td>
                  <td className="p-3">{r.wfh_hours.toFixed(1)}</td>
                  <td className="p-3 font-mono text-xs">{r.events_count}</td>
                  <td className="p-3">{r.leaves_by_type.ANNUAL || 0}</td>
                  <td className="p-3">{r.leaves_by_type.SICK || 0}</td>
                  <td className="p-3">{r.leaves_by_type.OVERTIME || 0}</td>
                </tr>
              ))}
              {!loading && (!data || data.rows.length === 0) && (
                <tr><td colSpan={9} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payrollPreview && (
        <div className="mt-6 brut-border bg-white brut-shadow overflow-hidden" data-testid="payroll-preview">
          <div className="p-4 border-b-2 border-black flex items-center justify-between bg-[#00CC33]">
            <div className="font-display font-black text-xl tracking-tighter">Payroll export preview · Prenos eTM</div>
            <div className="text-xs font-mono">{payrollPreview.rows.length} rows</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-[#F4F4F0] uppercase tracking-wider text-[10px]">
                <tr>
                  {payrollPreview.headers.map((h) => (
                    <th key={h} className="text-left p-2 border-b-2 border-black whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payrollPreview.rows.map((r, i) => (
                  <tr key={i} className="border-b border-black/10 hover:bg-[#FFCC00]/20">
                    {r.map((v, j) => (
                      <td key={j} className="p-2 whitespace-nowrap">
                        {v === null || v === undefined ? "" : String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
                {payrollPreview.rows.length === 0 && (
                  <tr><td colSpan={payrollPreview.headers.length} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t-2 border-black text-xs">
            <div className="font-bold uppercase tracking-wider mb-1">Payroll codes:</div>
            <div className="flex flex-wrap gap-2 font-mono">
              {Object.entries(payrollPreview.codes).map(([k, v]) => (
                <span key={k} className="tag bg-white">{k}: {v}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
